'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createClient } from '../../../lib/supabase/client';
import SiteNav from '@/components/SiteNav';
import LandingFooter from '../../landing-page/components/LandingFooter';

export default function AIAgentsUpdatePage() {
  const router = useRouter();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (!session || !session?.user?.email_confirmed_at) {
        router?.push('/conversion-page');
        return;
      }
      setLoading(false);
    });
  }, [router]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F7F4' }}>
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#D4891A', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F7F4' }}>
      <SiteNav variant="white" currentPath="/updates" />

      <div className="max-w-screen-md mx-auto px-6 lg:px-8 py-16 lg:py-24">
        {/* Back link */}
        <Link
          href="/updates"
          className="font-body text-sm transition-colors duration-150 hover:opacity-75 inline-flex items-center gap-1 mb-10"
          style={{ color: '#D4891A' }}
        >
          ← All Updates
        </Link>

        {/* Article header */}
        <div className="mb-10">
          <p
            className="font-body text-xs font-semibold uppercase tracking-widest mb-1"
            style={{ color: '#D4891A', letterSpacing: '0.14em' }}
          >
            ANNA UPDATE
          </p>
          <p className="font-body text-xs mb-5" style={{ color: '#9CA3AF' }}>
            May 2026
          </p>
          <h1
            className="font-display text-3xl lg:text-4xl font-semibold leading-tight mb-4"
            style={{ color: '#1B2A4A' }}
          >
            Everyone Is Talking About AI Agents. Here&rsquo;s What Actually Matters.
          </h1>
          <p className="font-body text-sm" style={{ color: '#6B7280' }}>
            By Anna Rippon, ThatClicked
          </p>
        </div>

        {/* Article body */}
        <div className="space-y-6 font-body text-base leading-relaxed" style={{ color: '#374151' }}>

          <p>
            If you have been reading the technology press lately, you will have noticed that AI agents are getting a significant amount of coverage. The word &ldquo;agentic&rdquo; is appearing everywhere. There are announcements from every major AI company. The coverage is breathless.
          </p>

          <p>
            Most of it can safely be ignored. Here is why — and here is the part that actually matters to you.
          </p>

          <h2 className="font-display text-2xl font-semibold mt-10 mb-3" style={{ color: '#1B2A4A' }}>
            What an AI agent actually is
          </h2>

          <p>
            An AI agent is a system that can take a sequence of actions on your behalf — not just answer a question, but actually do things. Book a meeting. Search the web. Send an email. Fill in a form. Run a multi-step task without you having to prompt it at each stage.
          </p>

          <p>
            The difference between a standard AI tool and an agent is roughly this: a standard AI tool responds to what you ask it. An agent can be given a goal and work toward it across multiple steps, making decisions along the way.
          </p>

          <p>
            This is genuinely new. It is also genuinely useful — in certain contexts, for certain kinds of work.
          </p>

          <h2 className="font-display text-2xl font-semibold mt-10 mb-3" style={{ color: '#1B2A4A' }}>
            Why most of the coverage can be ignored
          </h2>

          <p>
            The coverage is focused on what AI agents will eventually be able to do — autonomous systems managing entire workflows, replacing whole categories of work, operating independently at scale. This is the version that gets written about because it is dramatic.
          </p>

          <p>
            It is also, for most professionals right now, not the relevant version. The agentic AI that exists today is useful but limited. It makes mistakes. It requires oversight. It works well for structured, repeatable tasks and less well for anything requiring judgment, context, or professional nuance.
          </p>

          <p>
            The gap between what is being written about and what is actually available and reliable is large. That gap will close — but not as quickly as the coverage implies.
          </p>

          <h2 className="font-display text-2xl font-semibold mt-10 mb-3" style={{ color: '#1B2A4A' }}>
            What is actually relevant to you right now
          </h2>

          <p>
            There are two things worth paying attention to.
          </p>

          <p>
            <strong style={{ color: '#1B2A4A' }}>First:</strong> Microsoft Copilot is already moving in an agentic direction. If you use Copilot through work, you will start to see features that can take actions across applications — summarising a meeting and drafting follow-up emails, for example, or pulling information from multiple documents to produce a briefing. These are early agentic features. They are worth knowing about because they will become more capable over the next twelve months.
          </p>

          <p>
            <strong style={{ color: '#1B2A4A' }}>Second:</strong> The underlying shift matters even if the current tools are limited. The direction of travel is toward AI that does more of the routine cognitive work — the research, the drafting, the summarising, the scheduling — so that human attention can go to the things that require judgment, relationships, and experience. That is a shift that favours experienced professionals, not a threat to them.
          </p>

          <p>
            The professionals who will be most affected by agentic AI are those doing high volumes of structured, repeatable knowledge work. The professionals who will be least affected — and potentially most advantaged — are those whose value lies in judgment, context, and relationships. Which is most of the people reading this.
          </p>

          <h2 className="font-display text-2xl font-semibold mt-10 mb-3" style={{ color: '#1B2A4A' }}>
            What to do with this
          </h2>

          <p>
            Nothing urgent. Continue what you are doing — learning to use the tools that exist now, building the habit of working with AI, developing your own sense of what it is good for and what it is not.
          </p>

          <p>
            When agentic features appear in the tools you already use — and they will, gradually — you will be well placed to use them. Because you will already understand the underlying tools, you will already have a sense of when to trust AI output and when to check it, and you will already have the habit of working with AI rather than around it.
          </p>

          <p>
            That is the advantage of doing this work now, rather than waiting until the technology forces the issue.
          </p>

          <p className="font-medium italic mt-8" style={{ color: '#1B2A4A' }}>
            — Anna
          </p>
        </div>

        {/* Back to updates */}
        <div className="mt-14 pt-8" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Link
            href="/updates"
            className="font-body text-sm font-semibold transition-colors duration-150 hover:opacity-75"
            style={{ color: '#D4891A' }}
          >
            ← Back to Anna&rsquo;s Updates
          </Link>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}
